import {
  TraverseOptions,
  ViewpointRelationshipDiagramViewModel,
  GraphItemsModel,
  GraphItem,
  LegendComponentType,
  LegendReferenceType,
} from '@ardoq/graph';
import { isEqual, uniq } from 'lodash';
import { ALL_EXCEPT_PARENTS } from 'tabview/relationsD3View/consts';
import {
  COMMON_RELATIONSHIPS_DIAGRAM_EMPTY_STATE,
  VIEWPOINT_MODE_RELATIONSHIPS_DIAGRAM_EMPTY_STATE,
} from './consts';
import {
  DEFAULT_VIEWPOINT_MODE_SETTINGS,
  DEFAULT_WORKSPACE_MODE_SETTINGS,
} from 'views/environmentals/viewSettings/defaultStreamState';
import { WorkspaceModeViewKey } from 'views/environmentals/viewSettings/types';
import { getDefaultRelationshipDiagramsViewInterface } from './viewModel$/getDefaultRelationshipDiagramsViewInterface';
import { ViewpointRelationshipDiagramViewId } from 'tabview/relationshipsDiagramsViewModel/types';
import {
  getAdditionalRowsForGraphViewLegend,
  GetAdditionalRowsForGraphViewLegendArgs,
} from 'tabview/graphViews/graphViewLegend/graphViewLegendUtils';
import { ViewLegendRowModel } from '@ardoq/view-legend';

type ViewModelGraph<TNode extends GraphItem, TGroup extends GraphItem> = {
  nodes: GraphItemsModel<TNode>;
  groups: GraphItemsModel<TGroup>;
  componentIdsOfCollapsedGroups?: string[];
};
export const getComponentIdsFromViewModel = <
  TNode extends GraphItem,
  TGroup extends GraphItem,
>({
  nodes,
  groups,
  componentIdsOfCollapsedGroups = [],
}: ViewModelGraph<TNode, TGroup>) => {
  return uniq([
    ...[...nodes.add, ...nodes.update].map(id => nodes.byId.get(id)!.modelId),
    ...[...groups.add, ...groups.update]
      .map(id => groups.byId.get(id)!)
      .filter(group => group.isComponent())
      .map(group => group.modelId),
    ...componentIdsOfCollapsedGroups,
  ]);
};

const associateReferenceTypes = (
  referenceTypesIdentifiers: Set<string> | null,
  referenceTypeMap: Map<string, string[]>
) =>
  referenceTypesIdentifiers
    ? new Set(
        [...referenceTypesIdentifiers].flatMap(referenceType =>
          referenceType === ALL_EXCEPT_PARENTS
            ? [...referenceTypeMap.values()].flat()
            : referenceType
        )
      )
    : referenceTypesIdentifiers;

type OverrideRelationshipsTraverseOptionsArgs = {
  menuConfig: TraverseOptions;
  referenceTypeMap: Map<string, string[]>;
};
export const overrideRelationshipsTraverseOptions = ({
  menuConfig,
  referenceTypeMap,
}: OverrideRelationshipsTraverseOptionsArgs) => ({
  ...menuConfig,
  maxDegreesIncoming: 0, // this view does not allow individual degrees. but if the user had access to the beta, they might have a non-zero value left over in their settings.
  maxDegreesOutgoing: 0, // this view does not allow individual degrees. but if the user had access to the beta, they might have a non-zero value left over in their settings.
  outgoingReferenceTypes: associateReferenceTypes(
    menuConfig.outgoingReferenceTypes,
    referenceTypeMap
  ),
  incomingReferenceTypes: associateReferenceTypes(
    menuConfig.incomingReferenceTypes,
    referenceTypeMap
  ),
});

export const getViewpointRelationshipsDiagramFlatEmptyState = <
  TViewId extends ViewpointRelationshipDiagramViewId,
>(
  viewId: TViewId
): ViewpointRelationshipDiagramViewModel<
  (typeof DEFAULT_VIEWPOINT_MODE_SETTINGS)[TViewId]
> => {
  const viewSettings = DEFAULT_VIEWPOINT_MODE_SETTINGS[viewId];
  return {
    ...VIEWPOINT_MODE_RELATIONSHIPS_DIAGRAM_EMPTY_STATE,
    viewId,
    viewSettings,
    viewInterface: getDefaultRelationshipDiagramsViewInterface(),
  };
};

export const getDefaultRelationshipDiagramStateForWorkspaceMode = <
  TViewId extends WorkspaceModeViewKey,
>(
  viewId: TViewId
) => ({
  ...COMMON_RELATIONSHIPS_DIAGRAM_EMPTY_STATE,
  viewId,
  viewSettings: DEFAULT_WORKSPACE_MODE_SETTINGS[viewId],
});

type EnhanceLegendEntityTypesArgs = {
  initialComponentTypes: LegendComponentType[];
  initialReferenceTypes: LegendReferenceType[];
  selectedComponentTypes: string[][];
  selectedReferenceTypes: string[][];
};

const enhanceLegendEntityTypes = ({
  initialComponentTypes,
  initialReferenceTypes,
  selectedComponentTypes,
  selectedReferenceTypes,
}: EnhanceLegendEntityTypesArgs) => ({
  componentTypes: initialComponentTypes.map(componentType => ({
    ...componentType,
    isSelected: selectedComponentTypes.some(selected =>
      isEqual(selected, componentType.ids)
    ),
  })),
  referenceTypes: initialReferenceTypes.map(referenceType => ({
    ...referenceType,
    isSelected: selectedReferenceTypes.some(selected =>
      isEqual(selected, referenceType.globalTypeIds)
    ),
  })),
});

type GetRelationshipsViewLegendContentsArgs =
  GetAdditionalRowsForGraphViewLegendArgs & {
    selectedComponentTypes: string[][];
    selectedReferenceTypes: string[][];
  };

export const getRelationshipsViewLegendContents = ({
  componentTypes: initialComponentTypes,
  referenceTypes: initialReferenceTypes,
  selectedComponentTypes,
  selectedReferenceTypes,
  ...rest
}: GetRelationshipsViewLegendContentsArgs): ViewLegendRowModel[] => {
  const { componentTypes, referenceTypes } = enhanceLegendEntityTypes({
    initialComponentTypes,
    initialReferenceTypes,
    selectedComponentTypes,
    selectedReferenceTypes,
  });

  return getAdditionalRowsForGraphViewLegend({
    ...rest,
    componentTypes,
    referenceTypes,
  });
};
